import React, { useState, useEffect, useImperativeHandle, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useLocalization, type Language } from '../../config/localization';
import BaseWidget from './BaseWidget';
import { Icon, ICONS } from '../ui/icons';
import CustomSelect from '../ui/inputs/CustomSelect';
import Tooltip from '../ui/Tooltip';
import ImageSizeInput from '../ui/inputs/ImageSizeInput';
import SeedInput from '../ui/inputs/SeedInput';
import { parseDropdownOptions } from '../../utils/dropdownOptions';
import { isRecreateBlobForOrg } from '../../utils/recreateStorage';
import { clampSeed, nextRandomSeed, normalizeSeedBounds, offsetSeed } from '../../utils/seedRolling';
import { persistSeedAdvance, subscribeToSeedStorageSync } from '../../utils/seedStorageSync';
import type { IWorkflowWidget, WidgetPayloadResult, WidgetValidationError } from '../../interfaces/IWorkflowWidget';

interface WidgetField {
  id: number;
  widget_type_id: string;
  field_type: string;
  field_id: string;
  label: string;
  description: string | null;
  tooltip: string | null;
  display_order: number;
  is_required: boolean;
  config_data: string | null;
  default_value?: any;
}

interface WorkflowWidget {
  id: string;
  workflow_id: string;
  widget_type_id: number;
  is_enabled: boolean;
  display_order: number;
  config_data?: string;
  widget_type: {
    id: number;
    name: string;
    description?: string;
    display_type: 'small' | 'big';
    is_dynamic: boolean;
  };
}

// LEGACY: Renders fields from widget_dynamic_fields table (global widget types).
// New approach: use DynamicWidget which reads fields from workflow.dynamic_widget JSON (embedded).
interface DynamicWidgetRendererProps {
  widget: WorkflowWidget;
  selectedLanguage: Language;
  widgetId?: string;
  widgetRef?: React.RefObject<IWorkflowWidget>;
  className?: string;
  /**
   * Extra namespace for the localStorage value keys. Field values are persisted
   * per widget TYPE, so two workflows sharing a widget type share one saved
   * value. That is fine when one workflow is on screen, but Multi mounts N
   * models side by side and presents them as independent per-model settings —
   * without a scope, editing model A's value leaks into model B on its next
   * mount (and into the billed generation). Omitted → legacy unscoped keys, so
   * existing saved values on the classic pages are preserved.
   */
  storageScope?: string;
}

export default function DynamicWidgetRenderer({
  widget,
  selectedLanguage,
  widgetId,
  widgetRef,
  className = '',
  storageScope
}: DynamicWidgetRendererProps) {
  const { getToken } = useAuth();
  const { currentOrganization } = useOrganization();
  const t = useLocalization(selectedLanguage);
  const [fields, setFields] = useState<WidgetField[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  // Bumped by the Retry button — re-runs the field-loading effect after a
  // failed fetch (otherwise the widget stays not-ready forever).
  const [reloadNonce, setReloadNonce] = useState(0);
  const [values, setValues] = useState<Record<string, any>>({});
  const [fixedSeeds, setFixedSeeds] = useState<Record<string, boolean>>({});
  const valuesRef = useRef(values);
  const fixedSeedsRef = useRef(fixedSeeds);
  const mountedRef = useRef(true);
  valuesRef.current = values;
  fixedSeedsRef.current = fixedSeeds;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Single source of truth for the persisted-value key. `storageScope` (when
  // supplied) namespaces it per workflow so sibling models don't share values.
  const buildStorageKey = (fieldId: string, orgId: string | number) =>
    storageScope
      ? `dynamic_widget_${storageScope}_${widget.widget_type_id}_${fieldId}_${orgId}`
      : `dynamic_widget_${widget.widget_type_id}_${fieldId}_${orgId}`;

  const buildFixedStorageKey = (fieldId: string, orgId: string | number) =>
    `${buildStorageKey(fieldId, orgId)}_fixed`;

  useEffect(() => subscribeToSeedStorageSync(({ storageKey, value }) => {
    if (!currentOrganization) return;
    const seedField = fields.find(candidate => (
      candidate.field_type === 'seed'
      && buildStorageKey(candidate.field_id, currentOrganization.id) === storageKey
    ));
    if (seedField) {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return;
      const config = parseConfigData(seedField.config_data);
      const nextValue = clampSeed(numericValue, config.min_value, config.max_value);
      const nextValues = { ...valuesRef.current, [seedField.field_id]: nextValue };
      valuesRef.current = nextValues;
      setValues(nextValues);
    }
    // The fixed flag is tab-scoped (sessionStorage) — no cross-tab sync.
  }), [fields, currentOrganization, storageScope, widget.widget_type_id]);

  const captureSeedAdvance = (): (() => void) | null => {
    const snapshots = fields.flatMap(field => {
      if (field.field_type !== 'seed' || fixedSeedsRef.current[field.field_id]) return [];
      const config = parseConfigData(field.config_data);
      const { min, max } = normalizeSeedBounds(config.min_value, config.max_value);
      return [{ fieldId: field.field_id, seed: valuesRef.current[field.field_id], min, max }];
    });
    if (snapshots.length === 0) return null;

    return () => {
      snapshots.forEach(({ fieldId, seed, min, max }) => {
        if (fixedSeedsRef.current[fieldId] || !Object.is(valuesRef.current[fieldId], seed)) return;
        let nextSeed = nextRandomSeed(min, max);
        if (nextSeed === seed && min !== max) {
          nextSeed = offsetSeed(nextSeed, 1, min, max);
        }
        const persistedResult = currentOrganization
          ? persistSeedAdvance({
              storageKey: buildStorageKey(fieldId, currentOrganization.id),
              fixedStorageKey: buildFixedStorageKey(fieldId, currentOrganization.id),
              expectedSeed: seed,
              nextSeed,
            })
          : 'unavailable';
        if (persistedResult === 'unavailable') handleFieldChange(fieldId, nextSeed);
      });
    };
  };

  // Expose widget interface methods via ref
  useImperativeHandle(widgetRef, () => ({
    getPayloadData(): WidgetPayloadResult {
      const data: Record<string, any> = {};
      const seedFields: NonNullable<WidgetPayloadResult['seedFields']> = [];

      fields.forEach(field => {
        // Use current value or fall back to default value
        let value = values[field.field_id];

        // If no value set yet, use default_value from field
        if (value === undefined || value === null || value === '') {
          value = field.default_value;
        }

        // Only include if we have a value
        if (value !== undefined && value !== null && value !== '') {
          // Special handling for image_size fields
          if (field.field_type === 'image_size' && typeof value === 'object' && 'width' in value && 'height' in value) {
            // Split into separate width and height keys
            data[`${field.field_id}_width`] = value.width;
            data[`${field.field_id}_height`] = value.height;
          } else {
            data[field.field_id] = value;
            if (field.field_type === 'seed') {
              const config = parseConfigData(field.config_data);
              seedFields.push({
                payloadKey: field.field_id,
                min: config.min_value ?? 0,
                max: config.max_value ?? 2147483647,
              });
            }
          }
        }
      });

      return { data, ...(seedFields.length > 0 && { seedFields }) };
    },
    validateState(): WidgetValidationError[] {
      const errors: WidgetValidationError[] = [];
      fields.forEach(field => {
        if (field.is_required) {
          const value = values[field.field_id];
          if (value === undefined || value === null || value === '' || 
              (Array.isArray(value) && value.length === 0)) {
            errors.push({
              field: field.field_id,
              message: `${field.label} is required`
            });
          }
        }
      });
      return errors;
    },
    isRequired(): boolean {
      // Widget is required if it has any required fields
      return fields.some(field => field.is_required);
    },
    isSettingsReady(): boolean {
      // While the field list is still fetching (or the fetch failed), `fields`
      // is empty, so getPayloadData/validateState would report an empty-but-
      // "valid" widget. Callers that dispatch billed generations (Multi) must
      // treat that window as not-ready instead of valid.
      return !loading && !loadFailed;
    },
    hasSettingsLoadError(): boolean {
      // Distinguishes a PERMANENT not-ready (the fetch failed and will not
      // retry on its own) from the transient in-flight window. Both block a
      // billed dispatch, but only this one is actionable: the caller must point
      // the user at the in-accordion Retry button instead of telling them to
      // wait for something that will never resolve.
      return loadFailed;
    },
    advanceSeed(): void {
      captureSeedAdvance()?.();
    },
    captureSeedAdvance,
  }), [fields, values, fixedSeeds, loading, loadFailed]);

  // Load widget fields
  useEffect(() => {

    if (!widget.widget_type.is_dynamic) {
      setLoading(false);
      return;
    }

    const loadFields = async () => {
      try {
        setLoadFailed(false);
        console.log('Loading fields for widget type:', widget.widget_type_id);
        const token = await getToken({ template: 'withmail' });
        const response = await fetch(`/api/widgets/types/${widget.widget_type_id}/fields`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });


        if (response.ok) {
          const data = await response.json();
          const sortedFields = (data.fields || []).sort((a: WidgetField, b: WidgetField) => a.display_order - b.display_order);
          setFields(sortedFields);
          
          // Initialize values from widget config_data and localStorage
          const initialValues: Record<string, any> = {};
          const initialFixedSeeds: Record<string, boolean> = {};
          
          // First, load from widget config_data
          if (widget.config_data) {
            try {
              const configData = JSON.parse(widget.config_data);
              Object.assign(initialValues, configData);
            } catch (e) {
              // Error parsing widget config_data
            }
          }
          
          // Set default values from field configs
          sortedFields.forEach((field: WidgetField) => {
            const fieldConfig = parseConfigData(field.config_data);
            if (fieldConfig.default_value !== undefined && initialValues[field.field_id] === undefined) {
              initialValues[field.field_id] = fieldConfig.default_value;
            }
            if (field.field_type === 'seed' && field.default_value !== undefined && initialValues[field.field_id] === undefined) {
              initialValues[field.field_id] = field.default_value;
            }
          });
          
          // Then, override with localStorage values
          if (typeof window !== 'undefined' && currentOrganization) {
            sortedFields.forEach((field: WidgetField) => {
              try {
                const storageKey = buildStorageKey(field.field_id, currentOrganization.id);
                const storedValue = localStorage.getItem(storageKey);
                if (storedValue !== null) {
                  initialValues[field.field_id] = JSON.parse(storedValue);
                }
                if (field.field_type === 'seed') {
                  // sessionStorage on purpose: the lock lives only within the
                  // current tab and never survives a browser restart.
                  const storedFixed = sessionStorage.getItem(buildFixedStorageKey(field.field_id, currentOrganization.id));
                  if (storedFixed !== null) {
                    initialFixedSeeds[field.field_id] = JSON.parse(storedFixed) === true;
                  }
                }
              } catch (e) {
                // Failed to load from localStorage
              }
            });
          }

          sortedFields.forEach((field: WidgetField) => {
            const currentValue = initialValues[field.field_id];
            if (field.field_type !== 'seed') return;
            const fieldConfig = parseConfigData(field.config_data);
            const min = fieldConfig.min_value ?? 0;
            const max = fieldConfig.max_value ?? 2147483647;
            initialValues[field.field_id] = currentValue !== undefined && currentValue !== null && currentValue !== ''
              ? clampSeed(Number(currentValue), min, max)
              : nextRandomSeed(min, max);
            if (typeof window !== 'undefined' && currentOrganization) {
              try {
                localStorage.setItem(
                  buildStorageKey(field.field_id, currentOrganization.id),
                  JSON.stringify(initialValues[field.field_id])
                );
              } catch (e) {
                // Failed to save to localStorage
              }
            }
          });
          
          valuesRef.current = initialValues;
          fixedSeedsRef.current = initialFixedSeeds;
          setValues(initialValues);
          setFixedSeeds(initialFixedSeeds);
        } else {
          console.error('Failed to load fields, status:', response.status);
          const errorData = await response.text();
          console.error('Error response:', errorData);
          setLoadFailed(true);
        }
      } catch (error) {
        console.error('Failed to load fields exception:', error);
        setLoadFailed(true);
      } finally {
        setLoading(false);
      }
    };

    loadFields();
    // `storageScope` is a dep because loadFields reads persisted values through
    // buildStorageKey — without it a re-scoped instance (same React key, new
    // scope) would read the old key while writing the new one, which is exactly
    // the cross-model settings bleed storageScope exists to prevent.
  }, [widget.widget_type_id, widget.widget_type.is_dynamic, widget.config_data, getToken, currentOrganization, reloadNonce, storageScope]);

  // Handle recreate values from sessionStorage after fields are loaded
  useEffect(() => {
    if (fields.length === 0 || loading) return;
    // The stamp check below fails closed on an unknown organization and DELETES
    // the blob, so running before the org resolves would throw away a
    // legitimate same-org hand-off. The field fetch does not depend on the org,
    // so this effect can win that race; `currentOrganization` is already a dep,
    // so it simply re-runs once the id lands.
    if (!currentOrganization?.id) return;

    const dynamicValuesJson = sessionStorage.getItem('recreate-dynamic-values');
    if (!dynamicValuesJson) return;

    try {
      const dynamicValues = JSON.parse(dynamicValuesJson);

      // Check timestamp - settings older than 30 seconds are invalid
      if (Date.now() - dynamicValues.timestamp > 30000) {
        sessionStorage.removeItem('recreate-dynamic-values');
        return;
      }

      // The blob carries raw generation settings captured in ONE organization.
      // This effect re-runs on `currentOrganization`, and React flushes child
      // effects before the provider's purge, so a switch inside the 30s window
      // would otherwise apply the previous tenant's values here AND persist
      // them under the NEW org's storage key below. Reject anything not stamped
      // for the current organization (see `src/utils/recreateStorage.ts`).
      if (!isRecreateBlobForOrg(dynamicValues, currentOrganization?.id)) {
        sessionStorage.removeItem('recreate-dynamic-values');
        return;
      }

      if (dynamicValues.rawInput) {
        const rawInput = dynamicValues.rawInput;
        let hasUpdates = false;
        const newValues = { ...values };

        // Apply recreate values for fields that match
        fields.forEach(field => {
          if (rawInput[field.field_id] !== undefined) {
            let restoredValue = rawInput[field.field_id];
            if (field.field_type === 'seed') {
              const fieldConfig = parseConfigData(field.config_data);
              restoredValue = clampSeed(
                Number(restoredValue),
                fieldConfig.min_value,
                fieldConfig.max_value
              );
            }
            newValues[field.field_id] = restoredValue;
            hasUpdates = true;

            // Also save to localStorage for persistence
            if (typeof window !== 'undefined' && currentOrganization) {
              try {
                const storageKey = buildStorageKey(field.field_id, currentOrganization.id);
                localStorage.setItem(storageKey, JSON.stringify(restoredValue));
              } catch (e) {
                // Failed to save to localStorage
              }
            }
          }
        });

        if (hasUpdates) {
          valuesRef.current = newValues;
          setValues(newValues);
        }
      }

      // Clear after processing (other widgets may also need it, so use timeout)
      setTimeout(() => {
        sessionStorage.removeItem('recreate-dynamic-values');
      }, 2000);
    } catch (error) {
      console.warn('Failed to apply recreate dynamic values:', error);
    }
  }, [fields.length, loading, currentOrganization, widget.widget_type_id]);

  const parseConfigData = (configData: string | null): any => {
    if (!configData) return {};
    
    try {
      const parsed = JSON.parse(configData);
      if (typeof parsed === 'string') {
        return JSON.parse(parsed);
      }
      return parsed;
    } catch {
      return {};
    }
  };

  const handleFieldChange = (fieldId: string, value: any) => {
    const nextValues = { ...valuesRef.current, [fieldId]: value };
    valuesRef.current = nextValues;
    if (mountedRef.current) setValues(nextValues);
    
    // Save to localStorage
    if (typeof window !== 'undefined' && currentOrganization) {
      try {
        const storageKey = buildStorageKey(fieldId, currentOrganization.id);
        localStorage.setItem(storageKey, JSON.stringify(value));
      } catch (error) {
        // Failed to save to localStorage
      }
    }
  };

  const handleFixedChange = (fieldId: string, checked: boolean) => {
    const nextFixedSeeds = { ...fixedSeedsRef.current, [fieldId]: checked };
    fixedSeedsRef.current = nextFixedSeeds;
    if (mountedRef.current) setFixedSeeds(nextFixedSeeds);

    if (typeof window !== 'undefined' && currentOrganization) {
      try {
        sessionStorage.setItem(
          buildFixedStorageKey(fieldId, currentOrganization.id),
          JSON.stringify(checked)
        );
      } catch (error) {
        // Failed to save to sessionStorage
      }
    }
  };

  const renderField = (field: WidgetField) => {
    const config = parseConfigData(field.config_data);
    const value = values[field.field_id] !== undefined ? values[field.field_id] : (config.default_value !== undefined ? config.default_value : '');

    const fieldProps = {
      id: field.field_id,
      value,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange(field.field_id, e.target.value),
      required: field.is_required,
      className: "w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 hover:border-gray-500 focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500/20 transition-all duration-200 text-sm"
    };

    switch (field.field_type) {
      case 'string_field':
        const stringValue = value || '';
        const maxLength = config.max_length;
        const minLength = config.min_length || 0;
        const isMultiline = config.multiline ?? false;
        const textareaRows = config.rows ?? 3;
        const isStringOutOfRange = (maxLength && stringValue.length > maxLength) ||
                                   (minLength > 0 && stringValue.length < minLength && stringValue.length > 0);
        const stringErrorClass = isStringOutOfRange ? 'border-red-500 focus:border-red-400 focus:ring-red-500/20' : '';

        return (
          <div>
            {isMultiline ? (
              <textarea
                id={field.field_id}
                value={stringValue}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleFieldChange(field.field_id, e.target.value)}
                required={field.is_required}
                rows={textareaRows}
                maxLength={maxLength}
                placeholder=""
                className={`${fieldProps.className} resize-y min-h-[80px] ${stringErrorClass}`}
              />
            ) : (
              <input
                type="text"
                {...fieldProps}
                placeholder=""
                maxLength={maxLength}
                className={`${fieldProps.className} ${stringErrorClass}`}
              />
            )}
            {isStringOutOfRange && (
              <p className="text-xs text-red-400 mt-1">
                {minLength && stringValue.length < minLength
                  ? `Minimum length is ${minLength} characters`
                  : `Maximum length is ${maxLength} characters`
                }
              </p>
            )}
            {maxLength && !isStringOutOfRange && (
              <p className="text-xs text-gray-400 text-right mt-1">
                {stringValue.length}/{maxLength}
              </p>
            )}
          </div>
        );

      case 'int_field':
        const intValue = value !== '' && value !== null && value !== undefined ? parseInt(value) : null;
        const minValue = config.min_value ?? -Infinity;
        const maxValue = config.max_value ?? Infinity;
        const isIntOutOfRange = intValue !== null && (intValue < minValue || intValue > maxValue);
        
        return (
          <div>
            <input
              type="number"
              {...fieldProps}
              onChange={(e) => {
                const newValue = e.target.value === '' ? '' : parseInt(e.target.value) || 0;
                handleFieldChange(field.field_id, newValue);
              }}
              placeholder=""
              min={minValue !== -Infinity ? minValue : undefined}
              max={maxValue !== Infinity ? maxValue : undefined}
              className={`${fieldProps.className} ${isIntOutOfRange ? 'border-red-500 focus:border-red-400 focus:ring-red-500/20' : ''}`}
            />
            {isIntOutOfRange && (
              <p className="text-xs text-red-400 mt-1">
                {intValue < minValue 
                  ? `Minimum value is ${minValue}` 
                  : `Maximum value is ${maxValue}`
                }
              </p>
            )}
          </div>
        );

      case 'slider':
        const sliderMin = config.min_value || 0;
        const sliderMax = config.max_value || 100;
        const sliderStep = config.step || 1;
        const sliderDesign = config.design || 'basic';
        
        // For display purposes - show actual min/max even if step doesn't align.
        // Nullish (not ||) so a legit 0 is kept instead of snapping back to min.
        const displayValue = value ?? (config.default_value ?? sliderMin);
        // Round display value to avoid showing long decimals
        const roundedDisplayValue = Math.round(displayValue * 1000) / 1000;
        
        // Create a list of valid values including min and max
        const validValues: number[] = [];
        
        // Always include min
        validValues.push(sliderMin);
        
        // Add intermediate values
        let currentVal = sliderMin + sliderStep;
        while (currentVal < sliderMax) {
          // Round to avoid floating point precision issues
          validValues.push(Math.round(currentVal * 1000) / 1000);
          currentVal += sliderStep;
        }
        
        // Always include max
        if (validValues[validValues.length - 1] !== sliderMax) {
          validValues.push(sliderMax);
        }
        
        // Find closest valid value to current
        const findClosestValue = (target: number) => {
          return validValues.reduce((prev, curr) => 
            Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev
          );
        };
        
        const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const rawValue = parseFloat(e.target.value);
          const closestValue = findClosestValue(rawValue);
          // Round to avoid floating point precision issues
          const roundedValue = Math.round(closestValue * 1000) / 1000;
          handleFieldChange(field.field_id, roundedValue);
        };
        
        // Use a smaller step for the HTML range input to allow smoother movement
        const htmlStep = Math.min(sliderStep, (sliderMax - sliderMin) / 100);
        
        const sliderContent = (
          <>
            <input
              type="range"
              {...fieldProps}
              onChange={handleSliderChange}
              min={sliderMin}
              max={sliderMax}
              step={htmlStep}
              value={displayValue}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
            />
            <div className="flex justify-between text-xs text-gray-400">
              <span>{sliderMin}</span>
              <span className="font-medium text-white">
                {roundedDisplayValue}
              </span>
              <span>{sliderMax}</span>
            </div>
          </>
        );
        
        // Apply special design if selected
        if (sliderDesign === 'special1') {
          // Return slider without wrapper - the special wrapper will be applied at the field level
          return sliderContent;
        }
        
        // Default basic design
        return (
          <div className="space-y-2">
            {sliderContent}
          </div>
        );

      case 'dropdown':
        const dropdownOptions = parseDropdownOptions(config.options);

        // Set default value if not already set and default_value is provided
        const dropdownValue = value || config.default_value || '';

        return (
          <CustomSelect
            value={dropdownValue}
            onChange={(newValue) => handleFieldChange(field.field_id, newValue)}
            options={dropdownOptions}
            placeholder={config.placeholder || "Select option..."}
            className="w-full"
            language={selectedLanguage}
          />
        );

      case 'dropdown_prompt': {
        const dpOptions = Array.isArray(config.options)
          ? config.options
              .filter((opt: any) => opt.label && opt.prompt_id)
              .map((opt: any) => ({ label: opt.label, value: String(opt.prompt_id) }))
          : [];
        const dpValue = value || config.default_value || '';
        return (
          <CustomSelect
            value={dpValue}
            onChange={(newValue) => handleFieldChange(field.field_id, newValue)}
            options={dpOptions}
            placeholder={config.placeholder || "Select option..."}
            className="w-full"
            language={selectedLanguage}
          />
        );
      }

      case 'multiselect':
        const multiselectOptions = parseDropdownOptions(config.options);

        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            {multiselectOptions.map((option, idx) => (
              <label key={idx} className="flex items-center space-x-2 text-sm cursor-pointer group">
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.value)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...selectedValues, option.value]
                      : selectedValues.filter(v => v !== option.value);
                    handleFieldChange(field.field_id, newValues);
                  }}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-purple-600 focus:ring-2 focus:ring-purple-500/20 transition-all duration-200"
                />
                <span className="text-gray-300 group-hover:text-white transition-colors duration-200">{option.label}</span>
              </label>
            ))}
          </div>
        );

      case 'image_size':
        const imageSizeValue = value || { width: config.default_width || 768, height: config.default_height || 1024 };

        // Ensure backward compatibility: convert string array to object array
        let aspectRatios = config.aspect_ratios;
        if (aspectRatios && Array.isArray(aspectRatios) && aspectRatios.length > 0) {
          if (typeof aspectRatios[0] === 'string') {
            // Old format: convert to new format
            aspectRatios = aspectRatios.map((ratio: string) => ({
              ratio,
              width: null,
              height: null
            }));
          }
        }

        return (
          <ImageSizeInput
            value={imageSizeValue}
            onChange={(newValue) => handleFieldChange(field.field_id, newValue)}
            config={{
              min_width: config.min_width || 64,
              max_width: config.max_width || 2048,
              min_height: config.min_height || 64,
              max_height: config.max_height || 2048,
              default_width: config.default_width || 768,
              default_height: config.default_height || 1024,
              step: config.step || 8,
              aspect_ratios: aspectRatios,
              default_aspect_ratio: config.default_aspect_ratio
            }}
            className="w-full"
          />
        );

      case 'seed':
        const seedValue = value ?? config.default_value ?? null;
        return (
          <SeedInput
            value={seedValue}
            onChange={(newValue) => handleFieldChange(field.field_id, newValue)}
            checked={fixedSeeds[field.field_id] ?? false}
            onFixedChange={(checked) => handleFixedChange(field.field_id, checked)}
            fixedLabel={t.fixedSeedLabel}
            fixedTooltip={t.fixedSeedTooltip}
            config={{
              min_value: config.min_value ?? 0,
              max_value: config.max_value ?? 2147483647
            }}
            className="w-full"
          />
        );

      default:
        return (
          <div className="text-xs text-gray-500 italic">
            Unsupported field type: {field.field_type}
          </div>
        );
    }
  };

  // Content for non-dynamic widgets
  if (!widget.widget_type.is_dynamic) {
    return (
      <BaseWidget
        title={widget.widget_type.name}
        icon={<Icon name={ICONS.SETTINGS} size={16} className="text-current" /> as any}
        description={widget.widget_type.description}
        language={selectedLanguage}
        className={className}
        widgetId={widgetId}
      >
        <div className="text-center py-4 text-xs text-gray-500 bg-gray-900/30 rounded border border-gray-700/30">
          {t.workflow?.widgetComingSoon || 'Widget implementation coming soon'}
        </div>
      </BaseWidget>
    );
  }

  // Loading state
  const content = loading ? (
    <div className="text-center py-4 text-xs text-gray-400">
      Loading fields...
    </div>
  ) : loadFailed ? (
    <div className="text-center py-4 text-xs text-red-400 space-y-2">
      <div>{t.workflow?.widgetLoadFailed || 'Failed to load settings'}</div>
      <button
        type="button"
        onClick={() => { setLoading(true); setReloadNonce((n) => n + 1); }}
        className="px-3 py-1 rounded bg-gray-700 text-gray-200 hover:bg-gray-600 transition-colors"
      >
        {t.workflow?.widgetLoadRetry || 'Retry'}
      </button>
    </div>
  ) : fields.length === 0 ? (
    <div className="text-center py-4 text-xs text-gray-500">
      No fields configured
    </div>
  ) : (
    <div className="space-y-4">
      {fields.map((field, index) => {
        const fieldConfig = parseConfigData(field.config_data);
        const isHighlighted = fieldConfig.highlight === true;
        const isSpecialSlider = field.field_type === 'slider' && fieldConfig.design === 'special1';
        
        return (
          <React.Fragment key={field.id}>
            {/* Add divider between fields except for the first one */}
            {index > 0 && <div className="border-t border-gray-700/50"></div>}
            
            <div className={isHighlighted || isSpecialSlider ? "bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-500/40 rounded-lg p-4" : ""}>
              {/* Label and Tooltip */}
              <div className="flex items-center justify-between mb-2">
                <label className={`${isHighlighted || isSpecialSlider ? 'text-sm font-semibold text-blue-200' : 'text-xs font-medium text-gray-300'}`}>
                  {field.label}
                </label>
                {(field.tooltip || field.field_type === 'int_field' || field.field_type === 'string_field') && (
                  <Tooltip content={(() => {
                    let tooltipContent = field.tooltip || '';
                    const config = parseConfigData(field.config_data);
                    
                    if (field.field_type === 'int_field') {
                      const hasMin = config.min_value !== undefined && config.min_value !== null;
                      const hasMax = config.max_value !== undefined && config.max_value !== null;
                      
                      if (hasMin || hasMax) {
                        const rangeInfo = [];
                        if (hasMin) rangeInfo.push(`Min: ${config.min_value}`);
                        if (hasMax) rangeInfo.push(`Max: ${config.max_value}`);
                        
                        if (tooltipContent) {
                          tooltipContent += ` (${rangeInfo.join(', ')})`;
                        } else {
                          tooltipContent = `Allowed range: ${rangeInfo.join(', ')}`;
                        }
                      }
                    } else if (field.field_type === 'string_field') {
                      const hasMin = config.min_length && config.min_length > 0;
                      const hasMax = config.max_length && config.max_length > 0;
                      
                      if (hasMin || hasMax) {
                        const lengthInfo = [];
                        if (hasMin) lengthInfo.push(`Min: ${config.min_length} chars`);
                        if (hasMax) lengthInfo.push(`Max: ${config.max_length} chars`);
                        
                        if (tooltipContent) {
                          tooltipContent += ` (${lengthInfo.join(', ')})`;
                        } else {
                          tooltipContent = `Length requirements: ${lengthInfo.join(', ')}`;
                        }
                      }
                    }
                    
                    return tooltipContent;
                  })()}>
                    <div className={`w-4 h-4 ${isHighlighted || isSpecialSlider ? 'bg-blue-700/50 border-blue-500' : 'bg-gray-700/50 border-gray-600'} border rounded-full flex items-center justify-center cursor-help ${isHighlighted || isSpecialSlider ? 'hover:bg-blue-600/50' : 'hover:bg-gray-600/50'} transition-colors duration-200`}>
                      <span className={`text-xs font-medium ${isHighlighted || isSpecialSlider ? 'text-blue-200' : 'text-gray-300'}`}>?</span>
                    </div>
                  </Tooltip>
                )}
              </div>
              
              
              {/* Field Input */}
              {field.field_type === 'slider' && fieldConfig.design === 'special1' ? (
                <div className="space-y-2">
                  {renderField(field)}
                </div>
              ) : (
                renderField(field)
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );

  // Return dynamic widget with BaseWidget wrapper
  return (
    <BaseWidget
      title={widget.widget_type.name}
      icon={<Icon name={ICONS.SETTINGS} size={16} className="text-current" /> as any}
      description={widget.widget_type.description}
      language={selectedLanguage}
      className={className}
      widgetId={widgetId}
    >
      {content}
    </BaseWidget>
  );
}
